import React, {
  useRef,
  useState,
  type FC,
  type ReactElement,
  type ReactNode
} from 'react'
import { Panel, type ImperativePanelHandle } from 'react-resizable-panels'

import classNames from 'classnames'

import { Orientation, ResizeableHandler } from 'components/ResizeableHandler'

import { useAvailableComponents } from 'hooks/useAvailableComponents'

import { type Filterable } from '../SideBar/types'
import { PanelHeader } from './PanelHeader'
import { PanelSearch } from './PanelSearch'
import classes from './PanelWrapper.module.scss'

interface PanelWrapperProps {
  id: string
  header: string
  order: number
  collapsed?: boolean
  children: ReactElement<Filterable> | Array<ReactElement<Filterable>>
  searchEnabled?: boolean
  contextMenu?: ReactNode
}

const PanelWrapper: FC<PanelWrapperProps> = ({
  id,
  header,
  order,
  collapsed = false,
  children,
  searchEnabled = false,
  contextMenu
}) => {
  const { components } = useAvailableComponents()
  const [isOpen, setIsOpen] = useState(!collapsed)
  const [isSearchActive, setIsSearchActive] = useState<boolean>(false)

  const ariaControlsId = `sectionId-${id}-controls`
  const ariaLabelledById = `sectionId-${id}-header`

  const [searchTerm, setSearchTerm] = useState<string>('')
  const refPanel = useRef<ImperativePanelHandle>(null)

  return (
    <>
      <ResizeableHandler
        orientation={Orientation.HORIZONTAL}
        disabled={!isOpen}
        id={id}
      />

      {isSearchActive ? (
        <PanelSearch
          name={id}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onClear={() => {
            setIsSearchActive(false)
          }}
        />
      ) : (
        <PanelHeader
          header={header}
          id={id}
          ariaLabelledById={ariaLabelledById}
          onSearchSelect={() => {
            setIsSearchActive(true)
          }}
          isExpanded={isOpen}
          onExpandSelect={() => {
            setIsOpen(!isOpen)
          }}
          searchEnabled={searchEnabled}
          contextMenu={contextMenu}
        />
      )}
      {isOpen && (
        <Panel ref={refPanel} order={order} collapsible id={id}>
          <section
            id={ariaControlsId}
            aria-labelledby={ariaLabelledById}
            className={classNames(classes.PanelContent, {
              [classes['PanelContent--Empty']]: components.length === 0
            })}
            data-testid={`${id}-browser`}
            tabIndex={0}
          >
            {React.Children.map(children, (child: ReactElement<Filterable>) => {
              if (React.isValidElement(child)) {
                return React.cloneElement(child, {
                  searchTerm: isSearchActive ? searchTerm : undefined,
                  expanded: isSearchActive && !!searchTerm
                })
              }

              return child
            })}
          </section>
        </Panel>
      )}
    </>
  )
}

export default PanelWrapper
